import { AlertTriangle, RefreshCw } from "lucide-react";
import { useEffect } from "react";

interface BlockedContentProps {
  onRetry?: () => void;
  componentName?: string;
  error?: Error;
}

/**
 * BlockedContent - Component displayed when content is blocked by ad blocker
 * Provides user-friendly message and instructions on how to fix the issue
 */
export default function BlockedContent({
  onRetry,
  componentName,
  error,
}: BlockedContentProps) {
  // Log error to console for debugging
  useEffect(() => {
    if (error) {
      console.error("Content loading error:", {
        componentName,
        error,
        message: error.message,
        stack: error.stack,
      });
    }
  }, [error, componentName]);

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  return (
    <div className="min-h-[400px] flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        <AlertTriangle
          className="w-16 h-16 text-[rgba(255,255,255,0.6)] mx-auto mb-4"
          aria-hidden="true"
        />
        <div role="alert" aria-live="assertive">
          <h2 className="text-2xl font-bold text-white mb-2">Content Blocked</h2>
          <p className="text-lg text-[rgba(255,255,255,0.6)] mb-4">
            {componentName
              ? `Unable to load ${componentName}.`
              : "Unable to load this content."}
          </p>
          <p className="text-base text-[rgba(255,255,255,0.5)] mb-6">
            This may be caused by an ad blocker or browser extension blocking the content.
            Please try:
          </p>
          <ul className="text-left text-sm text-[rgba(255,255,255,0.5)] mb-6 space-y-2 max-w-sm mx-auto">
            <li className="flex items-start gap-2">
              <span className="text-white">•</span>
              <span>Disable your ad blocker for this site</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-white">•</span>
              <span>Whitelist balpha.co in your ad blocker settings</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-white">•</span>
              <span>Try using a different browser or incognito mode</span>
            </li>
          </ul>
        </div>
        <button
          onClick={handleRetry}
          className="inline-flex items-center gap-2 bg-white text-[#09091e] px-6 py-3 rounded-full font-medium hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#09091e]"
        >
          <RefreshCw className="w-4 h-4" />
          Retry
        </button>
      </div>
    </div>
  );
}
